import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { CircleAlert, CircleCheck, X } from "lucide-react"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

// The admin flash (M7): the notice or alert a mutation redirected with,
// rendered once by AdminShell above the page. It sticks to the top of the
// viewport, so a flag switch or a move made far down a list (preserveScroll)
// still shows its message. Callouts from the design system; each message can
// be dismissed and comes back with the next visit's flash.
export function AdminFlash() {
  const { props } = usePage<PageProps>()
  const notice = props.flash?.notice
  const alert = props.flash?.alert
  const [dismissed, setDismissed] = React.useState<{ notice: boolean; alert: boolean }>({
    notice: false,
    alert: false,
  })

  // A new visit brings a new flash (or none): show whatever it carries.
  React.useEffect(() => router.on("navigate", () => setDismissed({ notice: false, alert: false })), [])

  const showAlert = Boolean(alert) && !dismissed.alert
  const showNotice = Boolean(notice) && !dismissed.notice
  if (!showAlert && !showNotice) return null

  return (
    // mr-12 below lg keeps clear of the fixed navigation button (top-right).
    <div className="sticky top-3 z-20 mb-6 mr-12 space-y-2 lg:mr-0">
      {showAlert && (
        <FlashNote
          role="alert"
          tone="danger"
          text={alert as string}
          onDismiss={() => setDismissed((value) => ({ ...value, alert: true }))}
        />
      )}
      {showNotice && (
        <FlashNote
          role="status"
          tone="accent"
          text={notice as string}
          onDismiss={() => setDismissed((value) => ({ ...value, notice: true }))}
        />
      )}
    </div>
  )
}

function FlashNote({
  role,
  tone,
  text,
  onDismiss,
}: {
  role: "alert" | "status"
  tone: "accent" | "danger"
  text: string
  onDismiss: () => void
}) {
  const Icon = tone === "danger" ? CircleAlert : CircleCheck
  return (
    <div
      role={role}
      className={cn(
        "callout flex items-start gap-3 py-1 pl-4 pr-1 shadow-sm",
        tone === "danger" ? "callout-danger" : "callout-accent",
      )}
    >
      <Icon
        aria-hidden="true"
        className={cn(
          "mt-3.5 h-4 w-4 shrink-0",
          tone === "danger" ? "text-danger-display" : "text-accent-display",
        )}
      />
      <p className="min-w-0 flex-1 py-2.5">{text}</p>
      <button
        type="button"
        onClick={onDismiss}
        className="inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:text-ink-display focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        aria-label="Dismiss message"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
